import { olderGeneration, youngerGeneration } from '@/assets/messages';
import { PrimaryAnnotatedType, SecondaryAnnotatedType } from '@/components/annotated-type';
import { StoryQuestion, useStoryQuestion } from '@/components/microstory/question';
import {
  OnStepEnterExitArgs,
  ScrollGraphic,
  ScrollRoot,
  ScrollStep,
  ScrollSteps,
} from '@/components/microstory/scroll';
import { CenteredStoryText, StoryText } from '@/components/microstory/text';
import { SunriseChart, SunriseChartData } from '@/components/sunrise-chart';
import { useNumberFormatting } from '@/utils/use-number-formatting';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { useState } from 'react';

type OnlineOpportunitiesChapter2Props = {
  data: OnlineOpportunitiesData;
};

const getChartLabel = (reasons: { [key: string]: string }, reason: string) => {
  return t({
    id: 'online-opportunities.chapter-2.sunrise-chart-label',
    message: `% who say the internet helps a lot with {reason}`,
    values: { reason: reasons[reason] },
  });
};

export function OnlineOpportunitiesChapter2(props: OnlineOpportunitiesChapter2Props) {
  const { data } = props;
  const { i18n } = useLingui();

  const formatValue = useNumberFormatting('percent');

  const reasons = {
    education: t({ id: 'online-opportunities.education', message: 'Education' }),
    havingFun: t({ id: 'online-opportunities.having-fun', message: 'Having fun' }),
    beingCreative: t({ id: 'online-opportunities.being-creative', message: 'Being creative' }),
    socializing: t({ id: 'online-opportunities.socializing', message: 'Socializing' }),
  };

  const answers = [
    {
      id: 'education',
      label: t({ id: 'online-opportunities.question-2.answer-1', message: 'Education' }),
      value: data.dt_helps_with_education.younger,
    },
    {
      id: 'having-fun',
      label: t({ id: 'online-opportunities.question-2.answer-2', message: 'Having fun' }),
      value: data.dt_helps_with_having_fun.younger,
    },
    {
      id: 'being-creative',
      label: t({ id: 'online-opportunities.question-2.answer-3', message: 'Being creative' }),
      value: data.dt_helps_with_being_creative.younger,
    },
    {
      id: 'socializing',
      label: t({ id: 'online-opportunities.question-2.answer-4', message: 'Socializing' }),
      value: data.dt_helps_with_socializing.younger,
    },
  ];

  const { selectedAnswer, handleAnswer } = useStoryQuestion<(typeof answers)[number]>();

  const [chartData, setChartData] = useState<SunriseChartData>();

  const handleStepEnter = (evt: OnStepEnterExitArgs<SunriseChartData>) => {
    setChartData(evt.data);
  };

  return (
    <>
      <CenteredStoryText>
        <Trans id="online-opportunities.chapter-2.text-1">
          Digital tools and online services can benefit children in a number of ways.
        </Trans>
      </CenteredStoryText>

      <StoryQuestion
        question={t({
          id: 'online-opportunities.question-2.question',
          message: 'What do you think is the biggest upside to children being online?',
        })}
        answers={answers}
        onAnswer={handleAnswer}
        selectedAnswer={selectedAnswer}
        response={
          selectedAnswer?.id &&
          t({
            id: 'online-opportunities.question-2.response',
            message: `${formatValue(
              selectedAnswer?.value
            )} of young people also see this as a benefit!`,
          })
        }
      />

      <CenteredStoryText>
        <Trans id="online-opportunities.chapter-2.text-2">
          On average, a majority of <PrimaryAnnotatedType>young people</PrimaryAnnotatedType> say
          children benefit a lot from being online in all these areas:
        </Trans>
      </CenteredStoryText>

      <ScrollRoot>
        <ScrollGraphic>
          <SunriseChart formatValue={formatValue} data={chartData} />
        </ScrollGraphic>

        <ScrollSteps onStepEnter={handleStepEnter}>
          <ScrollStep
            data={[
              {
                label: getChartLabel(reasons, 'socializing'),
                data: [
                  {
                    label: youngerGeneration(i18n),
                    value: data.dt_helps_with_socializing.younger,
                  },
                ],
              },
            ]}
          >
            <StoryText>
              <Trans id="online-opportunities.chapter-2.text-3">
                <PrimaryAnnotatedType>Socializing,</PrimaryAnnotatedType>
              </Trans>
            </StoryText>
          </ScrollStep>

          <ScrollStep
            data={[
              {
                label: getChartLabel(reasons, 'beingCreative'),
                data: [
                  {
                    label: youngerGeneration(i18n),
                    value: data.dt_helps_with_being_creative.younger,
                  },
                ],
              },
            ]}
          >
            <StoryText>
              <Trans id="online-opportunities.chapter-2.text-4">
                <PrimaryAnnotatedType>being creative,</PrimaryAnnotatedType>
              </Trans>
            </StoryText>
          </ScrollStep>

          <ScrollStep
            data={[
              {
                label: getChartLabel(reasons, 'havingFun'),
                data: [
                  {
                    label: youngerGeneration(i18n),
                    value: data.dt_helps_with_having_fun.younger,
                  },
                ],
              },
            ]}
          >
            <StoryText>
              <Trans id="online-opportunities.chapter-2.text-5">
                &hellip;and <PrimaryAnnotatedType>having fun.</PrimaryAnnotatedType>
              </Trans>
            </StoryText>
          </ScrollStep>

          <ScrollStep
            data={[
              {
                label: getChartLabel(reasons, 'education'),
                data: [
                  {
                    label: youngerGeneration(i18n),
                    value: data.dt_helps_with_education.younger,
                  },
                ],
              },
            ]}
          >
            <StoryText>
              <Trans id="online-opportunities.chapter-2.text-6">
                Above all, young people are most likely to report{' '}
                <PrimaryAnnotatedType>education</PrimaryAnnotatedType> as a major upside of kids
                going online.
              </Trans>
            </StoryText>
          </ScrollStep>

          <ScrollStep
            data={[
              {
                label: getChartLabel(reasons, 'socializing'),
                data: [
                  {
                    label: youngerGeneration(i18n),
                    value: data.dt_helps_with_socializing.younger,
                  },
                ],
              },
              {
                data: [
                  {
                    label: olderGeneration(i18n),
                    value: data.dt_helps_with_socializing.older,
                  },
                ],
              },
            ]}
          >
            <StoryText>
              <Trans id="online-opportunities.chapter-2.text-7">
                <SecondaryAnnotatedType>People over 40</SecondaryAnnotatedType> also say the
                internet can benefit children a lot, but are less enthusiastic than young people.
                That's true regarding:
              </Trans>
            </StoryText>
          </ScrollStep>

          <ScrollStep
            data={[
              {
                label: getChartLabel(reasons, 'socializing'),
                data: [
                  {
                    label: youngerGeneration(i18n),
                    value: data.dt_helps_with_socializing.younger,
                  },
                ],
              },
              {
                data: [
                  {
                    label: olderGeneration(i18n),
                    value: data.dt_helps_with_socializing.older,
                  },
                ],
              },
            ]}
          >
            <StoryText>
              <Trans id="online-opportunities.chapter-2.text-8">
                <SecondaryAnnotatedType>Socializing,</SecondaryAnnotatedType>
              </Trans>
            </StoryText>
          </ScrollStep>

          <ScrollStep
            data={[
              {
                label: getChartLabel(reasons, 'beingCreative'),
                data: [
                  {
                    label: youngerGeneration(i18n),
                    value: data.dt_helps_with_being_creative.younger,
                  },
                ],
              },
              {
                data: [
                  {
                    label: olderGeneration(i18n),
                    value: data.dt_helps_with_being_creative.older,
                  },
                ],
              },
            ]}
          >
            <StoryText>
              <Trans id="online-opportunities.chapter-2.text-9">
                <SecondaryAnnotatedType>being creative,</SecondaryAnnotatedType>
              </Trans>
            </StoryText>
          </ScrollStep>

          <ScrollStep
            data={[
              {
                label: getChartLabel(reasons, 'havingFun'),
                data: [
                  {
                    label: youngerGeneration(i18n),
                    value: data.dt_helps_with_having_fun.younger,
                  },
                ],
              },
              {
                data: [
                  {
                    label: olderGeneration(i18n),
                    value: data.dt_helps_with_having_fun.older,
                  },
                ],
              },
            ]}
          >
            <StoryText>
              <Trans id="online-opportunities.chapter-2.text-10">
                <SecondaryAnnotatedType>having fun,</SecondaryAnnotatedType>
              </Trans>
            </StoryText>
          </ScrollStep>

          <ScrollStep
            data={[
              {
                label: getChartLabel(reasons, 'education'),
                data: [
                  {
                    label: youngerGeneration(i18n),
                    value: data.dt_helps_with_education.younger,
                  },
                ],
              },
              {
                data: [
                  {
                    label: olderGeneration(i18n),
                    value: data.dt_helps_with_education.older,
                  },
                ],
              },
            ]}
          >
            <StoryText>
              <Trans id="online-opportunities.chapter-2.text-11">
                and <SecondaryAnnotatedType>education.</SecondaryAnnotatedType>
              </Trans>
            </StoryText>
          </ScrollStep>
        </ScrollSteps>
      </ScrollRoot>
    </>
  );
}
